/*
 * Copyright 2026 Sony Corporation
 */

import { LoadAssetContainerAsync } from '@babylonjs/core/Loading/sceneLoader';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { SPLATFileLoader } from '@babylonjs/loaders/SPLAT/splatFileLoader.pure';
import type { AssetContainer } from '@babylonjs/core/assetContainer';
import type { Scene } from '@babylonjs/core/scene';

// Registers the glTF plugin with the loader.
import '@babylonjs/loaders/glTF/2.0';
// Registers the .babylon plugin, for an uploaded .babylon model.
import '@babylonjs/core/Loading/Plugins/babylonFileLoader';

import { MAX_ASSET_BYTES, type AssetKind, type UploadType } from '@ips-device-verse/twin-service/schema/assets';

import { cachedBlob } from '../services/asset-cache';
import { isSplat, recordSplatMaterial } from '../scene/splat';
import type { ApiClient } from '../services/api-client';

/**
 * The user's own assets, turned into geometry (3d-reconstruction.md §7.3).
 *
 * A library reference is an asset id. What to fetch, and which loader reads
 * it, come from the asset's row, never from the id or a URL: the resolver asks
 * the service, which answers with a presigned GET for a ready asset.
 */

export interface ResolvedLibraryAsset {
  kind: AssetKind;
  content_type: UploadType;
  url: string;
  /** Metres per unit of the file, from the known-distance tool (§6.4). */
  scale: number;
}

export type LibraryResolver = (assetId: string, signal: AbortSignal) => Promise<ResolvedLibraryAsset>;

export const libraryResolver =
  (api: ApiClient): LibraryResolver =>
  async (assetId, signal) => {
    const { asset, url } = await api.getAsset(assetId, signal);
    if (!url) {
      throw new Error(`${asset.name} is still uploading.`);
    }
    return { kind: asset.kind, content_type: asset.content_type, url, scale: asset.scale ?? 1 };
  };

/** What a library asset's roots hang from; a rescale sets its scaling and nothing else. */
const SCALE_NODE = 'library:scale';

interface ScaleMetadata {
  libraryAsset?: string;
}

/** The node carrying a loaded library asset's scale, for this asset or, without an id, for any. */
export const libraryScaleNode = (container: AssetContainer, assetId?: string): TransformNode | undefined =>
  container.transformNodes.find(
    (node) =>
      node.name === SCALE_NODE &&
      (assetId === undefined || (node.metadata as ScaleMetadata | null)?.libraryAsset === assetId)
  );

/** Hangs a container's roots from one node carrying the asset's scale. */
export const scaleContainer = (
  scene: Scene,
  container: AssetContainer,
  assetId: string,
  scale: number
): AssetContainer => {
  const roots = [...container.meshes, ...container.transformNodes].filter((node) => !node.parent);
  const node = new TransformNode(SCALE_NODE, scene);
  // Built in the scene, but it belongs to the container and enters the scene with it.
  scene.removeTransformNode(node);
  node.metadata = { libraryAsset: assetId } satisfies ScaleMetadata;
  node.scaling.setAll(scale);
  for (const root of roots) {
    root.parent = node;
  }
  container.transformNodes.push(node);
  return container;
};

/** An asset never changes once published, so its bytes are cached under its id, not the signed URL. */
const LIBRARY_CACHE = 'library:';

const download = async (assetId: string, url: string, signal: AbortSignal): Promise<ArrayBuffer> => {
  const blob = await cachedBlob(`${LIBRARY_CACHE}${assetId}`, async () => {
    const response = await fetch(url, { signal });
    if (!response.ok) {
      throw new Error(`The asset could not be downloaded (${response.status}).`);
    }
    return response.blob();
  });
  if (blob.size > MAX_ASSET_BYTES) {
    throw new Error('The asset is larger than this editor will load.');
  }
  return blob.arrayBuffer();
};

/**
 * Splats, as the pipeline writes them.
 *
 * - `keepInRam`: the mesh can only serialize the splat data it kept, and the
 *   build serializes it into the `.babylon`.
 * - `spzLibraryUrl: undefined`: otherwise the loader fetches its decoder from
 *   unpkg, which a published scene must not depend on. The built-in parser
 *   reads the pipeline's `.spz` v3.
 * - `flipY: true` means "leave y alone": the loader otherwise assumes a y-down
 *   capture, and the pipeline has already put up at +y (§6.4).
 */
const splatLoader = (): SPLATFileLoader =>
  new SPLATFileLoader({ keepInRam: true, spzLibraryUrl: undefined, flipY: true });

const loadSplat = async (scene: Scene, bytes: ArrayBuffer): Promise<AssetContainer> => {
  // Typed as text, but a binary plugin is handed the bytes.
  const container = await splatLoader().loadAssetContainerAsync(scene, bytes as unknown as string, '');
  for (const mesh of container.meshes) {
    if (isSplat(mesh)) {
      // The pipeline writes right-handed y-up, as glTF does. The glTF loader
      // mirrors x into Babylon's left-handed space and the splat loader does
      // not, so without this a splat and its mesh land mirrored apart.
      mesh.scaling.x *= -1;
      recordSplatMaterial(mesh);
    }
  }
  return container;
};

/** Loads a library asset into an unattached container, like the other sources. */
export const loadLibraryAsset = async (
  scene: Scene,
  assetId: string,
  resolve: LibraryResolver | undefined,
  signal: AbortSignal
): Promise<AssetContainer> => {
  if (!resolve) {
    throw new Error('Your library is not available here.');
  }
  const asset = await resolve(assetId, signal);
  if (asset.kind === 'video') {
    throw new Error('A video is reconstructed from, not placed.');
  }

  const bytes = await download(assetId, asset.url, signal);
  if (asset.kind === 'splat') {
    return scaleContainer(scene, await loadSplat(scene, bytes), assetId, asset.scale);
  }
  // A File rather than a URL: the extension is how the loader picks its plugin.
  const name = asset.content_type === 'model/gltf-binary' ? 'asset.glb' : 'asset.babylon';
  const container = await LoadAssetContainerAsync(new File([bytes], name, { type: asset.content_type }), scene);
  return scaleContainer(scene, container, assetId, asset.scale);
};
